import * as React from 'react'
import { useRouter } from 'next/router'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import { Close } from 'svgIcons'
import { useDeleteNewsItemMutation } from 'generated/graphql'
import Button from '@app.components/Button'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getWebviewHref } from '@app.modules/util'
import Modal, { IFModalProps } from 'app.components/Modal/Modal'
import { mobileBreakPoint } from 'app.styled/media'

interface DeleteNewsItemModalProps extends IFModalProps {
  newsItemId: string
}

const DeleteNewsItemModal: React.FC<DeleteNewsItemModalProps> = ({
  onClose,
  newsItemId,
  ...props
}) => {
  const router = useRouter()
  const isWebview = useIsWebview()
  const { enqueueSnackbar } = useSnackbar()
  const onError = useChannelError()

  const [deleteNewsItemMutate] = useDeleteNewsItemMutation({
    onCompleted: (data) => {
      if (
        router.pathname ===
        getWebviewHref('/channel/[channel-url]/[news-id]', isWebview)
      ) {
        router.push(
          getWebviewHref(
            `/channel/${data.deleteNewsItem.channel?.url}`,
            isWebview,
          ),
        )
      }
      enqueueSnackbar('소식이 삭제되었습니다.')
    },
    onError,
  })

  const handleConfirm = () => {
    deleteNewsItemMutate({
      variables: {
        deleteNewsItemId: newsItemId,
      },
    })
    if (onClose) onClose()
  }

  return (
    <Modal onClose={onClose} {...props}>
      <StyledWrapper className="common-modal">
        <div className="close-btn" onClick={onClose}>
          <Close />
        </div>
        <div className="modal-title">이 소식을 삭제하시겠어요?</div>
        <div className="modal-description">
          <p>소식을 삭제하는 경우 아래와 같이 조치됩니다.</p>
          <ul>
            <li>
              이 소식에 등록된 <span>댓글이 함께 삭제</span> 됩니다.
            </li>
            <li>
              이 소식의 <span>‘좋아요, 스크랩, 공유하기’ 데이터가 삭제</span>{' '}
              됩니다.
            </li>
          </ul>
        </div>
        <div className="btn-list">
          <Button color="grey" className="btn cancle" onClick={onClose}>
            취소
          </Button>
          <Button className="btn" onClick={handleConfirm}>
            확인
          </Button>
        </div>
      </StyledWrapper>
    </Modal>
  )
}

export default DeleteNewsItemModal

const StyledWrapper = styled.div`
  width: 100%;
  max-width: 620px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;

  .close-btn {
    align-self: flex-end;
    color: #999999;
    cursor: pointer;
    padding: 0 30px;
  }
  .modal-title {
    width: 100%;
    font-size: 24px;
    font-weight: 500;
    padding-bottom: 30px;
    text-align: center;
    border-bottom: 1px solid #eeeeee;
  }
  .modal-description {
    font-size: 16px;
    margin: 40px 0 30px;
    padding: 0 70px;
    width: 100%;
    p {
      margin-bottom: 12px;
    }
    li {
      position: relative;
      margin-bottom: 5px;
      span {
        color: #01a0ff;
      }
    }
  }
  .btn-list {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    .btn {
      font-size: 16px;
      padding: 13px 30px;
      &.cancle {
        background-color: #bbbbbb;
      }
    }
  }
  ${mobileBreakPoint} {
    max-width: 350px;
  }
`
